<template>
<div class="px-4 sm:px-8 lg:px-0">
    <div class="mx-auto my-12 w-full max-w-[1136px] bg-white rounded-lg px-6 py-12 sm:px-8 text-main">
        <div class="flex flex-col items-center gap-8 pt-12 pb-8 w-full">
            <!-- ステップインジケーター -->
            <div class="flex items-center">
                <div class="flex flex-col items-center gap-0.5 w-16">
                    <i class="fa-solid fa-circle-check text-xl text-gray-300"></i>
                    <div class="text-[10px] text-sub text-center leading-3">
                        <p>Google</p>
                        <p>連携</p>
                    </div>
                </div>
                <div class="w-8 border-t border-gray-300"></div>
                <div class="flex flex-col items-center gap-0.5 w-16">
                    <i class="fa-solid fa-circle-check text-xl text-gray-300"></i>
                    <div class="text-[10px] text-sub text-center leading-3">
                        <p>アカウント</p>
                        <p>作成</p>
                    </div>
                </div>
                <div class="w-8 border-t border-gray-300"></div>
                <div class="flex flex-col items-center gap-0.5 w-16">
                    <div class="w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center">
                        <span class="text-[10px] font-bold text-white leading-3">3</span>
                    </div>
                    <div class="text-[10px] font-bold text-sub text-center leading-3">
                        <p>チャンネル</p>
                        <p>申請</p>
                    </div>
                </div>
            </div>

            <!-- タイトル + 説明 -->
            <div class="flex flex-col items-center gap-4 text-center">
                <h1 class="font-bold text-2xl leading-8">YouTubeチャンネルの申請</h1>
                <div class="text-sm leading-5">
                    <p>記事を作成・配信するには、YouTubeチャンネルの申請が必要です。</p>
                    <p>利用開始の通知を受け取るメールアドレスを入力してください。</p>
                </div>
                <a
                    href="#"
                    class="text-sm text-blue-600 leading-5 cursor-pointer"
                    @click.prevent="goToInvitationWaiting"
                >
                    編集者として参加する方はこちら
                </a>
            </div>
        </div>

        <!-- エラーメッセージ -->
        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

        <div v-if="isLoadingPreview" class="py-12 text-center">
            <DataLoading />
        </div>
        <template v-else-if="preview">
            <!-- フォーム本体 -->
            <div class="mx-auto max-w-[768px] w-full">
                <!-- チャンネル情報プレビュー -->
                <div class="flex flex-col md:flex-row items-center gap-6 pb-6 w-full">
                    <div class="w-20 h-20 shrink-0 rounded-full bg-slate-200 flex items-center justify-center">
                        <img v-if="preview.channel_icon" :src="preview.channel_icon" class="w-full h-full rounded-full" alt="チャンネルアイコン">
                        <i v-else class="fa-solid fa-user text-3xl text-slate-600"></i>
                    </div>
                    <div class="flex-1 flex flex-col gap-2 text-sm leading-5 w-full">
                        <p class="font-bold">チャンネルURL</p>
                        <p class="break-all">{{ preview.channel_url }}</p>
                    </div>
                </div>

                <!-- 名前/肩書き -->
                <div class="flex flex-col gap-2 pb-6">
                    <div class="flex gap-2 items-center">
                        <label class="text-sm font-bold leading-5" for="channelName">名前/肩書き</label>
                        <span class="text-xs font-bold text-alert leading-4">必須</span>
                    </div>
                    <input
                        id="channelName"
                        v-model="form.channelName"
                        @blur="touched.channelName = true"
                        :class="['p-2 border rounded-sm w-full text-base leading-6 text-main bg-white', (touched.channelName && errors.channelName) ? 'border-alert' : 'border-gray-300']"
                        maxlength="50"
                    />
                    <p class="text-right text-sub text-xs leading-4">{{ form.channelName.length }}/50</p>
                    <p v-if="touched.channelName && errors.channelName" class="text-alert text-sm font-bold">{{ errors.channelName }}</p>
                </div>

                <!-- 通知メールアドレス -->
                <div class="flex flex-col gap-2 pb-6">
                    <div class="flex gap-2 items-center">
                        <label class="text-sm font-bold leading-5" for="applicationEmail">通知メールアドレス</label>
                        <span class="text-xs font-bold text-alert leading-4">必須</span>
                    </div>
                    <input
                        id="applicationEmail"
                        v-model="form.applicationEmail"
                        @blur="touched.applicationEmail = true"
                        type="email"
                        placeholder="echoes@example.com"
                        :class="['p-2 border rounded-sm w-full text-base leading-6 text-main bg-white placeholder:text-gray-300', (touched.applicationEmail && errors.applicationEmail) ? 'border-alert' : 'border-gray-300']"
                    />
                    <p class="text-xs text-sub leading-4">
                        ※@livedoor.co.jp からのメールが受信できるよう、事前に設定をご確認ください。
                    </p>
                    <p v-if="touched.applicationEmail && errors.applicationEmail" class="text-alert text-sm font-bold">{{ errors.applicationEmail }}</p>
                </div>
            </div>

            <!-- 送信ボタン -->
            <div class="flex justify-center pt-2 w-full">
                <button
                    type="button"
                    class="btn-primary px-8 py-3"
                    :disabled="!canSubmit || isSubmitting"
                    :class="[(!canSubmit || isSubmitting) ? 'disabled:btn-disabled disabled:btn-lg' : '']"
                    @click="submitApplication"
                >
                    チャンネルを申請する
                </button>
            </div>
        </template>
    </div>
</div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import ErrorMessage from './ErrorMessage.vue';
import DataLoading from './DataLoading.vue';
import {
    CHANNEL_APPLICATION_STATUS_APPROVED,
    CHANNEL_APPLICATION_STATUS_REJECTED,
} from '@/plugins/constants';

const router = useRouter();
const store = useStore();

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;

const preview = ref(null);
const isLoadingPreview = ref(true);
const isSubmitting = ref(false);
const errorMessage = ref('');

const form = reactive({
    youtubeChannelId: '',
    channelName: '',
    applicationEmail: '',
});
const errors = reactive({
    channelName: '',
    applicationEmail: '',
});
// #301: blur するまでエラー表示を抑制 (初回描画で赤字にしないため)
const touched = reactive({
    channelName: false,
    applicationEmail: false,
});

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// #301: watch で errors を導出 (computed の副作用禁止)
const validate = () => {
    errors.channelName = '';
    errors.applicationEmail = '';

    if (!form.channelName) {
        errors.channelName = 'チャンネル名は必須です。';
    } else if (form.channelName.length > 50) {
        errors.channelName = 'チャンネル名は50文字以内で入力してください。';
    }

    if (!form.applicationEmail) {
        errors.applicationEmail = 'メールアドレスは必須です。';
    } else if (!emailPattern.test(form.applicationEmail)) {
        errors.applicationEmail = 'メールアドレスの形式が正しくありません。';
    }
};
watch(
    () => [form.channelName, form.applicationEmail],
    validate,
    { immediate: true },
);

const canSubmit = computed(() => {
    return !errors.channelName && !errors.applicationEmail;
});

// #301: YouTubeチャンネルプレビュー情報を取得
const fetchPreview = async () => {
    isLoadingPreview.value = true;
    try {
        const accessToken = store.state.accessToken;
        const response = await axios.get(
            `${apiEndpoint}/channel-applications/youtube-preview/`,
            {
                headers: { 'Authorization': `Bearer ${accessToken}` },
            }
        );
        console.log(response.data);
        preview.value = response.data;
        form.youtubeChannelId = response.data.youtube_channel_id || '';
        form.channelName = response.data.channel_name || '';
    } catch (error) {
        console.error('YouTubeチャンネル情報の取得に失敗しました。', error);
        // #301: backend は {'error': '<CODE>', 'detail': '...'} 形式で返す
        const errCode = error.response?.data?.error;
        if (errCode === 'CHANNEL_NOT_SELECTED') {
            errorMessage.value = 'YouTubeチャンネルが選択されていません。ログインし直してチャンネルを選択してください。';
        } else if (errCode === 'TOKEN_EXPIRED') {
            errorMessage.value = 'Googleアカウント連携の有効期限が切れました。ログインし直してください。';
        } else if (errCode === 'YOUTUBE_API_FAILED') {
            errorMessage.value = 'YouTubeからチャンネル情報を取得できませんでした。時間をおいて再度お試しください。';
        } else if (errCode === 'ALREADY_APPLIED') {
            // #301: 既に申請済み → /me/ で現在ステータスを取り、対応画面へ自動遷移
            await redirectByApplicationStatus();
        } else {
            errorMessage.value = 'チャンネル情報の取得中にエラーが発生しました。';
        }
    } finally {
        isLoadingPreview.value = false;
    }
};

// #301: 申請ステータスを再取得し、現在状況に応じた画面に遷移
const redirectByApplicationStatus = async () => {
    try {
        const accessToken = store.state.accessToken;
        const response = await axios.get(
            `${apiEndpoint}/channel-applications/me/`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
        const data = response.data;
        store.commit('setApplicationStatus', data.application_status);
        store.commit('setWaitForInvitation', data.wait_for_invitation);
        store.commit('setShowApprovalAlert', data.show_approval_alert);
        if (data.application_email !== undefined) {
            store.commit('setApplicationEmail', data.application_email);
        }
        const applicationStatus = Number(data.application_status);
        if (applicationStatus === CHANNEL_APPLICATION_STATUS_APPROVED) {
            router.push({ name: 'ArticleManagement' });
        } else if (applicationStatus === CHANNEL_APPLICATION_STATUS_REJECTED) {
            router.push({ name: 'ChannelApplicationRejected' });
        } else {
            router.push({ name: 'ChannelApplicationPending' });
        }
    } catch (e) {
        console.error('申請ステータスの取得に失敗しました。', e);
        errorMessage.value = '既に申請済みです。';
    }
};

const submitApplication = async () => {
    // 送信直前に全フィールドを touched 扱いしてエラーを可視化
    touched.channelName = true;
    touched.applicationEmail = true;
    validate();
    if (errors.channelName || errors.applicationEmail || isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    try {
        const accessToken = store.state.accessToken;
        const payload = {
            youtube_channel_id: form.youtubeChannelId,
            channel_name: form.channelName,
            application_email: form.applicationEmail,
        };
        const response = await axios.post(
            `${apiEndpoint}/channel-applications/submit/`,
            payload,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );
        // Vuex state を申請中に更新
        store.commit('setApplicationStatus', response.data.application_status ?? 1);
        store.commit('setApplicationEmail', response.data.application_email ?? form.applicationEmail);
        store.commit('setWaitForInvitation', false);
        router.push({ name: 'ChannelApplicationPending' });
    } catch (error) {
        console.error('申請送信に失敗しました。', error);
        // #301: backend は {'error': '<CODE>', 'detail': '...'} 形式で返す
        const errCode = error.response?.data?.error;
        if (errCode === 'ALREADY_APPLIED') {
            errorMessage.value = '既に申請済みです。';
        } else if (errCode === 'CHANNEL_NOT_SELECTED') {
            errorMessage.value = 'YouTubeチャンネルが選択されていません。';
        } else if (errCode === 'TOKEN_EXPIRED') {
            errorMessage.value = 'Googleアカウント連携の有効期限が切れました。ログインし直してください。';
        } else if (errCode === 'YOUTUBE_API_FAILED') {
            errorMessage.value = 'YouTubeからチャンネル情報を取得できませんでした。時間をおいて再度お試しください。';
        } else {
            errorMessage.value = '申請の送信に失敗しました。時間をおいて再度お試しください。';
        }
    } finally {
        isSubmitting.value = false;
    }
};

// #301: 「編集者として参加する方はこちら」→ storeにフラグを立てて待機画面へ
const goToInvitationWaiting = () => {
    store.commit('setWaitForInvitation', true);
    router.push({ name: 'ChannelApplicationWaiting' });
};

onMounted(() => {
    fetchPreview();
});
</script>
